import React from 'react';
import { Box } from '@mui/material';
import Txt from './Txt';
import { StandardButton } from './StandardButton';
import { FONT } from './typeScale';

/**
 * The one empty state — an invitation, not an absence.
 *
 * A card with nothing to show used to say "No data" / "NO DATA DETECTED" /
 * "—" in a dozen different voices. This renders the same three things every
 * time: an optional glowing icon, a short title in plain words, one line on
 * what to do, and (when there is a real next action) a button that does it.
 * ChartCard has its own version of this layout for charts; keep the two in
 * step if either changes.
 */
export interface EmptyStateProps {
  icon?: React.ReactNode;
  title: string;
  text?: string;
  cta?: { label: string; onClick: () => void };
  /** A quieter second action (text button) beside the main one. */
  secondary?: { label: string; onClick: () => void };
  /** Tighter padding for empties inside a small card or list. */
  compact?: boolean;
  /** Accent for the icon glow and the button border (default cyan). */
  accent?: string;
}

const EmptyState: React.FC<EmptyStateProps> = ({ icon, title, text, cta, secondary, compact, accent = '#00E0FF' }) => (
  <Box sx={{ textAlign: 'center', py: compact ? 2 : 4, px: 2 }}>
    {icon && (
      <Box sx={{ color: accent, mb: 0.75, filter: `drop-shadow(0 0 10px ${accent}99)`, '& svg': { fontSize: compact ? 28 : 34 } }}>
        {icon}
      </Box>
    )}
    <Txt sx={{ fontSize: FONT.body, fontWeight: 600, color: '#fff' }}>{title}</Txt>
    {text && (
      <Txt sx={{ fontSize: FONT.small, color: 'rgba(255,255,255,0.55)', mt: 0.25, lineHeight: 1.5 }}>{text}</Txt>
    )}
    {(cta || secondary) && (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 1, mt: 1.5, flexWrap: 'wrap' }}>
        {cta && (
          <StandardButton
            variant="outlined"
            size="small"
            onClick={cta.onClick}
            sx={{
              border: `1.5px solid ${accent}59 !important`,
              color: `${accent} !important`,
              '&:hover': { border: `1.5px solid ${accent} !important`, backgroundColor: `${accent}1A` },
            }}
          >
            {cta.label}
          </StandardButton>
        )}
        {secondary && (
          <StandardButton variant="text" size="small" onClick={secondary.onClick} sx={{ color: 'rgba(255,255,255,0.6)' }}>
            {secondary.label}
          </StandardButton>
        )}
      </Box>
    )}
  </Box>
);

export default EmptyState;
